import {
  DiameterNominal,
  GSize,
  InstallationPosition,
  MeasuringCurrentTransformer,
  Mounting,
  Orientation,
  Resistance,
  TubeMaterial,
} from '@enmon/commons/lib/shared/meter';
import {
  HardwareCategory,
  InventoryMeterMountType,
  type SerializedManufacturer,
  type SerializedMeterModel,
} from '@enmon/commons/lib/tools';
import type { DefaultOperatorName, Field } from 'react-querybuilder';
import {
  SHARED_ENUM_FIELD_CONFIG,
  SHARED_NUMBER_FIELD_CONFIG,
  SHARED_TEXT_FIELD_CONFIG,
  enumToOptions,
} from 'routes/reseller-data/shared-field-config';
import {
  createDeviceCategoryField,
  createLorawanRssiField,
  createMeasurementCategoryField,
  createMeterMountTechnologyField,
  createNbiotRssiField,
  createOwnerField,
  createOwnerTypeField,
  createCommodityField,
} from 'routes/reseller-data/common-fields';
import type { ConditionFieldName } from 'routes/reseller-data/const';
import { MeterAccessibility } from '@enmon/commons/lib/enmon';

export const getFields = (
  manufacturers: SerializedManufacturer[],
  meterModels: SerializedMeterModel[],
  meterAddons: any,
  hardwareCategory: HardwareCategory,
): Field<ConditionFieldName, DefaultOperatorName>[] => {
  switch (hardwareCategory) {
    case HardwareCategory.Module:
      return [
        {
          name: 'meter_addon_id',
          label: 'Meter Addon',
          values: meterAddons
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(({ id, name }) => ({ name: id, label: name })),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
      ];
    case HardwareCategory.Sensor:
      return [
        createCommodityField(),
        createLorawanRssiField(),
        createNbiotRssiField(),
        createMeterMountTechnologyField(),
      ];
    case HardwareCategory.Splitter:
      return [createMeterMountTechnologyField()];

    default:
      return [
        createCommodityField(), // TODO: !WARNING! CHECK POSSIBLY BREAKING CHANGE
        {
          name: 'accessibility',
          label: 'Accessibility',
          values: enumToOptions(MeterAccessibility),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        createOwnerTypeField(),
        createOwnerField(false),
        {
          name: 'new_meter',
          label: 'Is suggested new meter',
          defaultValue: false,
          operators: [{ name: '=', label: '=' }],
          valueEditorType: 'checkbox',
        },
        {
          name: 'manufacturer',
          label: 'Manufacturer',
          // @ts-expect-error sry querybuilder but I want my IDs to be number, not string...
          values: manufacturers
            ?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(({ id, name }) => ({ name: id, label: name })),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'meterModel',
          label: 'Model',
          // @ts-expect-error sry querybuilder but I want my IDs to be number, not string...
          values: meterModels
            ?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(({ id, name }) => ({ name: id, label: name })),
          ...SHARED_ENUM_FIELD_CONFIG,
          defaultValue: '',
        },
        { name: 'manufactured', label: 'Manufactured', ...SHARED_NUMBER_FIELD_CONFIG },
        { name: 'calibrated', label: 'Calibrated', ...SHARED_NUMBER_FIELD_CONFIG },
        {
          name: 'meter_mount_type',
          label: 'Mount type',
          values: enumToOptions(InventoryMeterMountType),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        createMeterMountTechnologyField(),
        {
          name: 'diameterNominal',
          label: 'Diameter nominal',
          values: enumToOptions(DiameterNominal),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'flange',
          label: 'Flange',
          defaultOperator: '=',
          defaultValue: false,
          operators: [{ name: '=', label: '=' }],
          valueEditorType: 'checkbox',
        },
        {
          name: 'flowrate',
          label: 'Flowrate',
          ...SHARED_TEXT_FIELD_CONFIG,
          inputType: 'flowrate',
        },
        {
          name: 'woltman',
          label: 'Woltman',
          defaultOperator: '=',
          defaultValue: false,
          operators: [{ name: '=', label: '=' }],
          valueEditorType: 'checkbox',
        },
        {
          name: 'layLength',
          label: 'Lay length',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'hotWater',
          label: 'Hot water',
          defaultOperator: '=',
          defaultValue: false,
          operators: [{ name: '=', label: '=' }],
          valueEditorType: 'checkbox',
        },
        {
          name: 'tubeMaterial',
          label: 'Tube material',
          values: enumToOptions(TubeMaterial),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'phases',
          label: 'Phases',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'mounting',
          label: 'Mounting',
          values: enumToOptions(Mounting),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'width',
          label: 'Width',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'iMin',
          label: 'I min',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'iRef',
          label: 'I ref',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'iMax',
          label: 'I max',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'circuitBreakerSize',
          label: 'Circuit breaker size',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'tariffs',
          label: 'Tariffs',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'isDirect',
          label: 'Direct',
          defaultOperator: '=',
          defaultValue: false,
          operators: [{ name: '=', label: '=' }],
          valueEditorType: 'checkbox',
        },
        {
          name: 'inputSpec',
          label: 'Input spec',
          defaultOperator: '=',
        },
        {
          name: 'measuringCurrentTransformer',
          label: 'Mct',
          values: enumToOptions(MeasuringCurrentTransformer),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        createDeviceCategoryField(),
        createMeasurementCategoryField(),
        {
          name: 'gSize',
          label: 'G size',
          values: enumToOptions(GSize),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'orientation',
          label: 'Orientation',
          values: enumToOptions(Orientation),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'pMax',
          label: 'P max',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'qMax',
          label: 'Q max',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'qMin',
          label: 'Q min',
          ...SHARED_NUMBER_FIELD_CONFIG,
        },
        {
          name: 'installationPosition',
          label: 'Installation position',
          values: enumToOptions(InstallationPosition),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
        {
          name: 'resistance',
          label: 'Resistance',
          values: enumToOptions(Resistance),
          ...SHARED_ENUM_FIELD_CONFIG,
        },
      ];
  }
};
